import { useState, useCallback, useEffect, useRef } from 'react';

import {
    LETTERS,
    type QtiMultipleSelectQuestionDTO,
    type UserInteractionMetaDTO,
} from '../../models';
import {
    SimpleCheckbox,
    SimpleSwitch,
    SimpleButton,
    ErrorMessage,
    type MenuAction,
    type ContextMenuInputItem,
    TTSButton,
    Loading,
    StatusMessage,
} from '@/src/ui';
import { DynamicTextPreview, type QuestionRendererOptions, defaultOptions } from '@/src/lib';
import { apiRequest } from '@/src/services';
import { submitForGrading } from '../../services';

interface MultipleSelectRendererProps {
    question: QtiMultipleSelectQuestionDTO;
    handleUpdateUserResponse?: (response: string, didUserInteract: boolean) => void;
    setQuestionLoading?: (loading: boolean) => void;
    loadedUserResponse?: string;
    loadedUserInteractionMeta?: UserInteractionMetaDTO;
    options?: QuestionRendererOptions;
}

export default function MultipleSelectRenderer({
    question,
    setQuestionLoading = () => {},
    handleUpdateUserResponse,
    loadedUserResponse,
    loadedUserInteractionMeta,
    options = defaultOptions,
}: MultipleSelectRendererProps) {
    const {
        disabled = false,
        hidePrompt = false,
        showLanguageToggle = false,
        showAudioButtons = false,
        showScoring = false,
        language = 'en',
    } = options;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [selectedChoiceIds, setSelectedChoiceIds] = useState<number[]>([]);
    const [strikedQuestionIds, setStrikedQuestionIds] = useState<number[]>([]);

    // Language may need to be hanldled internally if it is not controlled from the outside
    const [internalLanguage, setInternalLanguage] = useState<'en' | 'es'>(language);
    useEffect(() => {
        setInternalLanguage(language);
    }, [language]);

    // Use if scoring needs to be handled internally
    const [userResponse, setUserResponse] = useState('');
    if (!handleUpdateUserResponse) {
        handleUpdateUserResponse = setUserResponse;
    }

    const handleCheckboxClick = (choiceId: number, checked: boolean) => {
        userInteractedRef.current = true;
        setSelectedChoiceIds((prev) => {
            if (checked) {
                return prev.includes(choiceId) ? prev : [...prev, choiceId];
            } else {
                return prev.filter((id) => id !== choiceId);
            }
        });
    };

    // Manage save data
    const userInteractedRef = useRef(false);
    useEffect(() => {
        if (loadedUserResponse) {
            const parsed = JSON.parse(loadedUserResponse);
            if (parsed.selectedChoiceIds) {
                setSelectedChoiceIds(parsed.selectedChoiceIds);
            }
        }
        if (loadedUserInteractionMeta) {
            setStrikedQuestionIds(loadedUserInteractionMeta.strikedQuestionIds || []);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (selectedChoiceIds.length) {
            handleUpdateUserResponse(
                JSON.stringify({ selectedChoiceIds }),
                userInteractedRef.current
            );
        } else {
            handleUpdateUserResponse('', userInteractedRef.current);
        }
        userInteractedRef.current = false;

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedChoiceIds]);

    const handleChoiceStrike = (choiceId: number) => {
        (async function () {
            const newStrikedQuestionIds = [...strikedQuestionIds];
            if (newStrikedQuestionIds.includes(choiceId)) {
                const index = newStrikedQuestionIds.indexOf(choiceId);
                if (index > -1) {
                    newStrikedQuestionIds.splice(index, 1);
                }
            } else {
                newStrikedQuestionIds.push(choiceId);
            }
            setStrikedQuestionIds(newStrikedQuestionIds);

            try {
                if (loading) return;
                setLoading(true);

                setQuestionLoading(true);
                setError('');

                const userInteractionMeta = {
                    strikedQuestionIds: newStrikedQuestionIds,
                };

                await apiRequest<UserInteractionMetaDTO>(
                    'patch',
                    `assessments/studentExamView/updateUserInteractionMeta/${question.id}`,
                    userInteractionMeta
                );
            } catch {
                setError('Failed to toggle user interaction state.');
            } finally {
                setQuestionLoading(false);
                setLoading(false);
            }
        })();
    };

    const strikeAction: MenuAction<ContextMenuInputItem> = {
        label: (
            <div className="flex items-center justify-between w-full">
                <span>Strike through</span>
                <s className="ml-auto">abc</s>
            </div>
        ),
        onClick: (choice) => handleChoiceStrike(choice?.id || -1),
    };

    return (
        <>
            {showLanguageToggle && question.meta.isSpanishAllowed && (
                <div className="flex flex-row gap-x-8">
                    <div className="ml-auto">
                        <SimpleSwitch
                            name="toggleSpanish"
                            checked={internalLanguage === 'es'}
                            onChange={(e) => setInternalLanguage(e.target.checked ? 'es' : 'en')}
                            label="View Spanish"
                        />
                    </div>
                </div>
            )}
            {!hidePrompt && (
                <div
                    className="mb-4 flex flex-row items-end"
                    onContextMenu={(e) => {
                        if (options.onRendererContextMenu) {
                            e.preventDefault();
                            e.stopPropagation();
                            options.onRendererContextMenu(
                                e,
                                {
                                    language: internalLanguage,
                                    text: question.prompt,
                                    text_es: question.prompt_es,
                                },
                                []
                            );
                        }
                    }}
                >
                    <DynamicTextPreview
                        content={internalLanguage === 'en' ? question.prompt : question.prompt_es}
                    />
                    {showAudioButtons && (
                        <TTSButton
                            text={internalLanguage === 'en' ? question.prompt : question.prompt_es}
                            onError={setError}
                        />
                    )}
                </div>
            )}
            <div className="space-y-2">
                {question.choices.map((choice, index) => {
                    const letter = LETTERS[index];
                    const isChecked = selectedChoiceIds.includes(choice.id);
                    return (
                        <div
                            key={choice.id}
                            className={`w-full m-0 px-2 py-1 rounded flex items-center ${isChecked ? 'bg-blue-50' : ''}`}
                            onContextMenu={(e) => {
                                if (options.onRendererContextMenu) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    options.onRendererContextMenu(
                                        e,
                                        {
                                            id: choice.id,
                                            language: internalLanguage,
                                            text: choice.text,
                                            text_es: choice.text_es,
                                        },
                                        [strikeAction]
                                    );
                                }
                            }}
                        >
                            {strikedQuestionIds.includes(choice.id) ? (
                                <span className="font-bold w-6 text-center mr-2 line-through text-gray-400">
                                    {letter}
                                </span>
                            ) : (
                                <span className="font-bold w-6 text-center mr-2">{letter}</span>
                            )}
                            <SimpleCheckbox
                                name={`preview-selected-${choice.id}`}
                                label=""
                                checked={isChecked}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    handleCheckboxClick(choice.id, checked);
                                }}
                                disabled={strikedQuestionIds.includes(choice.id) || disabled}
                            />
                            <DynamicTextPreview
                                content={internalLanguage === 'en' ? choice.text : choice.text_es}
                            />
                            {showAudioButtons && (
                                <TTSButton
                                    text={internalLanguage === 'en' ? choice.text : choice.text_es}
                                    onError={setError}
                                />
                            )}
                        </div>
                    );
                })}
                <MultipleSelectScoring
                    question={question}
                    userResponse={userResponse}
                    showScoring={showScoring}
                />
                <ErrorMessage error={error} />
            </div>
        </>
    );
}

interface MultipleSelectScoringProps {
    question: QtiMultipleSelectQuestionDTO;
    userResponse: string;
    showScoring: boolean;
}

function MultipleSelectScoring({
    question,
    userResponse,
    showScoring,
}: MultipleSelectScoringProps) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isCorrect, setIsCorrect] = useState(false);
    const [answerChecked, setAnswerChecked] = useState(false);
    useEffect(() => {
        setAnswerChecked(false);
    }, [userResponse]);

    const parsedResponse = userResponse ? JSON.parse(userResponse) : null;
    const selectedChoiceIds = parsedResponse?.selectedChoiceIds || [];

    const handleCheckAnswer = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const { isCorrect: isDeterminedCorrect } = await submitForGrading(
                question,
                userResponse
            );
            setIsCorrect(isDeterminedCorrect);
            setAnswerChecked(true);
        } catch {
            setError('Failed to check answer.');
            setIsCorrect(false);
        } finally {
            setLoading(false);
        }
    }, [question, userResponse]);

    const isChoiceSelected = selectedChoiceIds.length > 0;

    if (!showScoring || question.meta.isHandScored === true) return null;

    return (
        <div className="mt-4 flex items-center space-x-4">
            <div>
                <SimpleButton
                    color="success"
                    onClick={handleCheckAnswer}
                    disabled={!isChoiceSelected || answerChecked || loading}
                    variant="contained"
                    className="px-4 py-1 rounded disabled:opacity-50"
                >
                    Check Answer
                </SimpleButton>
                <Loading visible={loading} message="Checking answer..." />
            </div>

            <ErrorMessage error={error} />
            <StatusMessage
                color={isCorrect ? 'green' : 'red'}
                visible={answerChecked && isChoiceSelected}
                status={isCorrect ? 'Correct!' : `Incorrect`}
            />
        </div>
    );
}
